import { Icon, MUIButton, MUIList, MUITextField, Title } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import { z } from "zod"
import abbLogo from "../../../assets/logos/abb-logo.png"
import { useAccessRequestCheck } from "../../../hooks/useAccessRequestCheck"
import { digitalManagerApi } from "../../../services/api"
import { DiMaProgress } from "../../components/DiMaProgress"
import { UserContext } from "../../context/UserContext"
import { ControllerRendererProps } from "../../types/reactHookForm"

const REASON_MIN_LENGTH = 30
const REASON_MAX_LENGTH = 300

const validationSchema = z.object({
  reason: z
    .string()
    .min(REASON_MIN_LENGTH, "Reason is too short")
    .max(REASON_MAX_LENGTH, "Reason is too long"),
})

export function NotAuthorized() {
  const { user, isLoadingUser } = useContext(UserContext)

  const { existAccessRequest, setAccessRequest, isLoadingResponse } =
    useAccessRequestCheck()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<{
    reason: string
  }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      reason: "",
    })
  }, [reset])

  const onSubmit = (values: { reason: string }) => {
    digitalManagerApi
      .post(`/backOfficeAccess/requests`, { reason: values.reason })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          setAccessRequest()
        }
      })
      .catch(() => {
        toast.error("Cannot send backoffice access request")
      })
  }

  const handleReasonChange = useCallback(
    (field: ControllerRenderProps<{ reason: string }, "reason">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderReasonTextField = useCallback(
    ({ field }: ControllerRendererProps<{ reason: string }, "reason">) => (
      <MUITextField
        id="reason"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleReasonChange(field)}
        onBlur={field.onBlur}
        placeholder="Describe why you need access to the Digital Manager backoffice, specifying which apps and features you need."
        multiline
        rows={4}
        errors={
          typeof errors.reason?.message === "string"
            ? { reason: { message: errors.reason.message } }
            : undefined
        }
      />
    ),
    [errors.reason, handleReasonChange]
  )

  if (isLoadingResponse || isLoadingUser || !user) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "50%",
        }}
      >
        <DiMaProgress />
      </div>
    )
  }

  if (user.hasAccessToDima) {
    return <Navigate to="/" />
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "10%",
      }}
    >
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable
        theme="colored"
      />
      <img src={abbLogo} alt="ABB Company Logo" />
      {existAccessRequest ? (
        <Title
          textElement={
            <span
              className="text-body1"
              style={{ textAlign: "center", marginTop: "16px" }}
            >
              Your request has been received and is being processed.
            </span>
          }
        />
      ) : (
        <>
          <MUIList
            listItems={[
              {
                id: "0",
                title:
                  "You do not have access to Digital ELSP Manager Backoffice, please fill the form and request the access.",
                description: "",
              },
            ]}
            size={32}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <div style={{ flex: "0 0 65%" }}>
              <Controller
                name="reason"
                control={control}
                render={renderReasonTextField}
              />
            </div>
          </div>
        </>
      )}
      {existAccessRequest ? (
        <Icon iconName="icon_abb_dias icon-abb_completed-check" size={32} />
      ) : (
        <div style={{ marginTop: "3%", display: "flex", justifyContent: "center" }}>
          <MUIButton
            label="Request Access"
            iconName="icon_abb_dias icon-abb_lock"
            variant="primary"
            size={40}
            isLoading={isSubmitting}
            disabled={!isValid}
            onClickButton={handleSubmit(onSubmit)}
          />
        </div>
      )}
    </div>
  )
}
